import {TIconType} from 'components/dynamic-icon/dynamic-icon';
import {EEditableInput} from 'components/editable-table';
import {IFormButtonWatcher} from 'components/form/buttons/use-form-button-watcher';
import {TEntity, TEntityValue} from 'modules/data';
import {ERequestStatuses} from 'modules/request-form/request-form.constants';
import {EEntityType} from 'shared/constants/entities';
import {ISortQuery, ERequestType} from 'shared/types/requests';
import {IFormFieldWatcher} from 'shared/types/watchers';
import {IBreadcrumbs} from 'store/slices/breadcrumbs-slice';

import {
    EAlign, EButtonType, EFieldActionType, EFieldType,
    EMetaActionType,
    EViewType,
} from './metadata-constants';
import {IFieldTooltipMetadata} from './tooltip-metadata-types';

export interface IMetadataState {
    [key: string]: IMetadataEntityState;
}

export interface IMetadataEntityState {
    [EEntityType.FORM]: IFormEntityMeta | IRequestFormEntityMeta;
    [EEntityType.EDIT_FORM]: IFormEntityMeta;
    [EEntityType.FORM_TABLE]: IFormTableEntityMeta;
    [EEntityType.TABLE]: ITableEntityMeta;
    [EEntityType.EDITABLE_TABLE]: IModalEntityMeta;
    [EEntityType.TABS]: IDirectoryEntityMeta;
    [EEntityType.TABS_WITH_REFERENCES]: IEntityMeta;
    [EEntityType.REFERENCE]: IEntityMeta;
    [EEntityType.NUMBER]: IEntityMeta;
    [EEntityType.FILTER]: IFilterEntityMeta;
    [EEntityType.CONTEXT_FILTER]: IContextFilterEntityMeta;
    [EEntityType.SEARCH]: ISearchEntityMeta;
    [EEntityType.TABLE_STATE]: IEntityMeta;
    [EEntityType.REPORT]: IEntityMeta;
    [EEntityType.JASPER_REPORT]: IJasperReportEntityMeta;
    [EEntityType.LIST]: IListEntityMeta;
    [EEntityType.LIST_GRID]: IListEntityMeta;
    [EEntityType.MODAL]: IModalEntityMeta;
    [EEntityType.SUBSECTION_TABLE]: ISubsectionTableEntityMeta;
    [EEntityType.TABS_WITH_FORM]: IDirectoryEntityMeta;
    [EEntityType.DASHBOARD]: IEntityMeta;
    [EEntityType.TABLE_REPORT]: IEntityMeta;
    [EEntityType.WIDGETS]: IEntityMeta;
    [EEntityType.FORM_FIELDS_DATA]: IFormEntityMeta;
}

/**
 * Общая структура метаданных
 */
export interface IEntityMeta {
    resetFormAfterSubmit?: boolean;
    title?: string;
    dynamicFormTitle?: IDynamicTitle;
    titleStructure?: string;
    titleField?: string;
    name: string;
    parentEntityName?: string;
    /**
     * Сущности для таблиц
     * Используется для работы с пагинацией
     */
    isPageJumperEnabled?: boolean;
    isPageSizeChangerEnabled?: boolean;
    disabledPagination?: boolean;
    /**
     * Ключ поля, значение которого влияет на отображаемые поля в контексте списка.
     */
    dataKeyForFilterFields?: string;
    /**
     * Для форм определяет какое поле главное и будет использоваться как id.
     * Для таблиц используется как параметр для ссылки
     */
    linkField?: string;
    /**
     * Это поле используется на бэке для
     * разграничения данных для api
     */
    viewType?: EViewType;
    fields: IFieldMeta[];
    widgetsConfig?: IWidgetsConfigMeta;
    /**
     * Используется для конфигураци запросов на другой хост.
     */
    urlConfig?: IUrlConfig;
    /**
     * Используется для форм, задает вкалдки формы.
     */
    tabs?: ITabsMeta[];
    useContext?: boolean;
    useBreadcrumbs?: boolean;
    actions?: IMetaActionInfo[];
    additionalOptions?: IAdditionalOptions;
    prefix?: string;
    filterParamKey?: string;
    duplicateButtons?: boolean;
    defaultFilters?: IDefaultFilters[];
    shouldLoadExtensions?: boolean;
    subsections?: ISubsectionMenu[];
    discardDateAndUserInfo?: boolean;
    checkForDisabledMetaFields?: boolean;
    /**
     * поля используется в форме, для отправки запроса о прочтении открытого элемента
     * */
    isUpdateNotificationStatus?: boolean;
    tableEntityName?: string;
    /**
     * поле активации редактирования карточки, без открытия карточки создания
     * */
    useDifferentForms?: boolean;
    /**
     * размер модального окна
     * */
    width?: number;
    isCentered?: boolean;
    isDraft?: boolean;
}

export interface IDefaultFilters {
    key: string | any;
    value: string;
}

export interface IUrlConfig {
    baseUrl?: string;
    prefix?: string;
    contextUrl?: string;
}

export type TVisibleMetaActionProp = 'default' | 'viewing' | 'creating' | 'editing';

/**
 * Общая структура данных для действий
 */
export interface IMetaActionInfo {
    entityName?: string;
    linkedEntityName?: string;
    linkField?: string;
    visible?: TVisibleMetaActionProp;
    actionType: EMetaActionType;
    actionTitle: string;
    actionCode: string;
    actionIcon?: TIconType | string;
    /**
     * Добавляется к entityName
     */
    childName?: string;
    order?: number;
    referenceUrl?: string;
    requestType?: ERequestType;
    urlParamKey?: string;
    childrens?: IMetaActionInfo[];
    /**
     * Имя модального окна, для действия
     */
    modalName?: string;
    /**
     * Сущность для модального окна.
     * Если нужно использовать имя сущности отличное от страницы.
     */
    modalEntityName?: string;
    /**
     * На основе этого параметра определяется
     * способ отображения фильтра (модальное окно или строка)
     */
    isIntegratedFilter?: boolean;

    /**
     * Параметр определяющий наличие
     * дополнительных ключей, которые
     * должны сохраняться для каждой записи
     */
    additionalInfoKeys?: string[];
    /** Использовать ли в качестве источника данных для additionalInfoKeys данные фильтра */
    shouldPickAdditionalInfoKeysFromFilter?: boolean;
    /**
     * Позволяет игнорировать настройки urlConfig
     */
    shouldIgnoreUrlConfig?: boolean;
    /** Список обязательных параметров, в отсутствии которых action блокируется (или любое иное действие) */
    requiredQueryParams?: string[];

    /**
     * Только для формы запросов
     */
    shouldDefaultRequestSave?: boolean;
    /**
     * Параметр нужен только для работы с конструктором метаданных,
     * что бы не создавать лишний вложенный список.
     * На бэк не должен передаваться.
     */
    shouldResetContext?: string;
    checkForDisabledMetaFields?: boolean;
    isSubmenu?: boolean;
    neededParamsForForm?: {
        from: string;
        to: string;
    }[];
    /**
     * Параметр нужен если мы хотим передавать заголовок application/json
     * вместо multipart/form-data
     */
    parentEntityType?: string;
    parentEntityName?: string;
    isJsonRequest?: boolean;
    selectedReferenceUrl?: string;
    mainReferenceUrl?: string;
    isResetFormValue?: boolean;
    buttonType?: EButtonType;
    tabKey?: string;
    title?: string;
    modalWidth?: string;
    /**
     * Для подстановки id в запросе непосредственно в url (/somePath/{id})
     */
    useUrl?: boolean;
    /**
     * Название ключа для массива id; по умолчанию - ids
     */
    itemListKey?: string;
    watchers?: IFormButtonWatcher[];
    /**
     * По key достаем LinkDto из выбранной строки
     */
    key?: string;
    /**
     * Флаг отвечающий за перезагрузку таблицы после запроса для типа API_REQUEST_BUTTON
     */
    shouldReloadTable?: boolean;
}

export interface IDirectoryEntityMeta extends IEntityMeta {
    titleField: string;
}

export const ActionVisibility: {
  CREATION: TVisibleMetaActionProp;
  EDITING: TVisibleMetaActionProp;
  VIEWING: TVisibleMetaActionProp;
  DEFAULT: TVisibleMetaActionProp;
} = {
    DEFAULT: 'default',
    VIEWING: 'viewing',
    EDITING: 'editing',
    CREATION: 'creating',
};

interface ISubsection {
    name: string;
    referenceUrl: string;
    parentId: string;
}

export interface IBreadcrumb {
    name: string;
    referenceUrl: string;
    subsection?: ISubsection[];
    path?: string;
}

export interface ITableEntityMeta extends IEntityMeta {
    isFilterable?: boolean;
    isSearchable?: boolean;
    isSelectable?: boolean;
    disabledPagination?: boolean;
    isPageJumperEnabled?: boolean;
    isPageSizeChangerEnabled?: boolean;
    isEditTable?: boolean;
    defaultSort?: ISortQuery;
    breadcrumbs?: IBreadcrumbs[];
    dependentLinkField?: string;
    isEditable?: boolean;
    prefix?: string;
    modalName?: string;
    parentEntityName?: string;
    checkForDisabledMetaFields?: boolean;
    allowEditTableInterface?: boolean;
    subsections?: ISubsectionMenu[];
    dashboardEntityName?: string;
    rowSelectionWatchers?: IFormFieldWatcher[];
}

export interface IModalEntityMeta extends IEntityMeta {
    cols: IColsMeta;
}

export interface IJasperReportEntityMeta extends IEntityMeta {
    referenceUrl?: string;
}

export interface IFormEntityMeta extends IEntityMeta {
    parentEntityName?: string;
    reportComparision?: { // todo: моковая структура для вкладки настроек сравнения
        listReferenceKey: string;
        attributeFieldKey: string;
        keyFieldKey: string;
        addKeyButtonKey: string;
        addAttributeButtonKey: string;
    };
}

export interface IListEntityMeta extends IEntityMeta {
}

export interface IInformationFieldMeta extends IFieldMeta {
    /**
     * Делает поле редактируемым при просмотре.
     * Нужно для работы с запросами, так как по умолчанию редактировать поля нельзя.
     */
    editableViewField?: boolean;
    /**
     * Делает поле не активным при создании
     */
    isDisabled?: boolean;
}

export interface IRequestFormEntityMeta extends IFormEntityMeta {
    isEditable?: boolean;
    isViewing?: boolean;
    preFillingOptions?: {
        /**
         * На основе этого имени выбираются данные для дочернего формы из родительской
         */
        parentEntityName?: string;
        shouldUpdateParentFormAfterSaving?: string;
        fieldNameForParamsRequest?: string;
        /**
         * Список полей для пред заполнения данных дочерней формы,
         * если данных нет, то идет обращение к api пред заполнения
         */
        matchingFields?: {
            from: string; // Название поля родительского элемента
            to: string; // Новое названия поля для дочерней формы
        }[];
        /**
         * Будет пред заполнение через sendReplay
         */
        shouldRemoteData?: boolean;
    };
    // TODO удалить если не используется
    statusesActions?: ERequestStatuses[];
    fields: IInformationFieldMeta[];
    /**
     * Нужно для отображения ответа при просмотре запроса
     */
    additionalForm?: IRequestFormEntityMeta;
    /**
     * Ключ, значение которого будет использоваться
     * в качестве заголовка
     */
    dataKeyForTitle?: string;
    /**
     * Ключ, значение которого будет использоваться
     * в качестве оценки
     */
    dataKeyForGrade?: string;
    dataKeyForGradeId?: string;
    dataKeyForFilterFields?: string;
}

export interface IFormTableEntityMeta extends ITableEntityMeta, IFormEntityMeta {
}

export interface ISubsectionTableEntityMeta extends ITableEntityMeta, IFormEntityMeta {
}

export interface IFilterEntityMeta extends IEntityMeta {
    filterByKeyField?: string;
}

export interface IContextFilterEntityMeta extends IFilterEntityMeta {
}

export interface ISearchEntityMeta extends IEntityMeta {
}

export type TFieldStructureType = {
    prefixForField?: string;
    fieldKey: string;
    iconName: string;
    hideLabel?: boolean;
    referenceUrl?: string;
    iconColor?: string;
    iconSize?: number;
    attachName?: string;
};

export interface IReferencePath {
    label?: string;
    value?: string;
    reserveLabel?: string;
    lookupCode?: string;
}

export interface IListFieldMeta {
    /**
     * Ключ поля, значение которого влияет на отображаемые поля в контексте списка.
     */
    dataKeyForFilter?: string;
    /**
     * Поля по умолчанию, которое отображается всегда.
     */
    defaultItems?: IFieldMeta[];
    /**
     * Объект в котором хранится список ключей и значений,
     * названия ключей строятся на основе значения поля соответствующего dataKeyForFilter.
     * А значение это массив полей, которые должны отображаться.
     */
    customItems?: { [valueFromFieldForFilter: string]: IFieldMeta[] };
}

export interface IGroupFieldMeta {
    key: string;
    label: string;
    items?: IFieldMeta[];
}

export interface IColsMeta {
    title: string;
    dataIndex: string;
    key: string;
    align?: EAlign;
    inputType?: EEditableInput;
    width?: number;
    editable?: boolean;
    alwaysEditable?: boolean;
    inputProps?: {
        labelPath: string;
        valuePath: string;
    };
    ellipsis?: boolean;
    getFieldState?: boolean;
}

/**
 * Общая структура данных для полей
 */
interface IAdditionalField {
    paramKey?: string;
    isAdditionalFieldsFromRequest?: boolean;
    referenceUrl?: string;
    additionalFields?: IFieldMeta[];
    isChangingFormStructure?: boolean;
    // Параметр для сохранения значений в полях, при изменении структуры формы. Связан с isChangingFormStructure.
    keepVisibleValues?: boolean;
}

export interface IAdditionalOptions {
    optionsField: IAdditionalField;
    discardEntityCreatedDate?: boolean;
    paramKey?: string;
}

export interface IFieldMeta extends IListFieldMeta, IGroupFieldMeta {
    actions?: IFieldAction[];
    isSortable?: boolean;
    key: string;
    label: string;
    hideLabel?: boolean;
    order?: number; // not implemented on back meta
    hint?: string;
    index?: number;
    isHidden?: boolean;
    isRequired?: boolean;
    required?: boolean; // for upload modals metadata
    isClearable?: boolean;
    isDisabled?: boolean;
    isEditDisabled?: boolean;
    isNotDisplay?: boolean;
    type: EFieldType;
    defaultValue?: TEntityValue;
    referenceUrl?: string;
    stringStructure?: string;
    editable?: boolean;
    additionalOptions?: IAdditionalOptions;
    dependentKeys?: string[];
    modalName?: string;
    modalEntityName?: string;
    breadcrumbsEntityName?: string;
    parentEntityName?: string;
    propertyCode?: string;
    propertyCodeValue?: string;
    propertyCodeList?: string[];
    customColor?: string;
    isChecked?: boolean;
    editModeWidth?: number;
    checkForDisabledMetaFields?: boolean;
    watchers?: IFormFieldWatcher[];

    /**
     * Используется для COMBINED_STRING
     * В качестве ключа используется ключ поля,
     * для значения которого нужна подпись
     */
    labels?: Record<string, string | undefined>;
    fieldStructure?: TFieldStructureType[];
    booleanValues?: string[];
    dateFormat?: string;
    requestDateFormat?: string; // Формат даты для запроса
    color?: string;
    /**
     * Параметр для поля REFERENCE
     */
    isFilterable?: boolean;
    multipleMode?: boolean;
    displayAfterActions?: boolean; // Параметр для формы запросов
    referenceParamsDefault?: Record<string, any>; // Вспомогательные значения для referenceParamsKeys
    referenceParamsKeys?: IReferenceParamKey[];
    path?: IReferencePath; // Определяет по каким ключам брать значения из объекта для select
    referenceValueKey?: string; // Ключ, который используется вместо id
    onNotCreateKey?: boolean;
    onNotCreateId?: boolean;
    displayFieldKey?: string;
    dependentInputKey?: string;
    requestFieldKey?: string; // Определяет по какому ключу отправлять значение (REFERENCE)
    requestKey?: string; // Определяет какое название ключа отправлять при запросе (REFERENCE)
    additionalInputOptions?: IAdditionalInputOptions[];
    generateOptionsRules?: IGenerateOptionsRulesProps;
    isSetFilter?: boolean;
    useContext?: boolean; // Добавлять ли в запрос на получение опций organizationId и periodId
    useFirstOptionAsDefaultValue?: boolean; // Использовать ли первое значение из dropdown как дефолтное
    isDisabledWhenSingle?: boolean; // Использовать единственное значение как дефолтное и запретить редактирование
    isFieldWithoutValue?: boolean;
    defaultValueByRule?: string;
    hideSearch?: boolean;
    /**
     * Параметры для UPLOAD_LIST
     */
    uploadUrl?: string; // url для загрузки фала
    dataKeyForDownload?: string; // ключ, значение которого будет в качестве параметра запроса
    uploadSignUrl?: string;
    /**
     * Параметры для FILE_LIST_WITH_SIGNS
     */
    downloadAllText?: string;
    downloadAllTextForFile?: string;
    downloadAllUrl?: string;
    /**
     * Параметр(ы) для поля STATIC_SELECT
     */
    options?: {
        label: string;
        value: string;
    }[];

    isDependsOnMainFilter?: string;
    /**
     * Параметры для FILE
     */
    buttonText?: string;
    shouldFastUpdate?: boolean;
    accept?: string;
    /**
     * Параметры валидации и поля Input.PASSWORD
     */
    validationMask?: string;
    validationErrorMessage?: string;
    requiredMatchPasswordField?: string;
    validationRules?: IValidationRules[];
    /**
     * Доп. параметры валидации
     */
    validationRegExpKey?: string;
    isNeedToRedirect?: string;
    /**
     * Параметр для добавления ссылки к полям в таблице по условию
     */
    redirectOnCondition?: IRedirectOnCondition;
    /**
     * Параметр(ы) для поля DEPENDENTS
     */
    dependentsFieldKey?: string;
    mask?: string;
    children?: IFieldMeta[];
    title?: string;
    dataIndex?: string;
    align?: EAlign;
    inputType?: EEditableInput;
    width?: number | string;
    alwaysEditable?: boolean;
    inputProps?: {
        labelPath: string;
        valuePath: string;
    };
    ellipsis?: boolean;
    getFieldState?: boolean;
    /**
     * Параметры условного ренденига поля
     */
    nonRenderable?: boolean;
    renderOnCondition?: IRenderProperties; // рендер по флагу от сервера
    placeholder?: string;
    isNotAvailableWhen?: IFieldAvailabilityCondition[];
    tooltip?: IFieldTooltipMetadata;
    fixedDropdown?: boolean;
    buttonType?: EButtonType;
    buttonIcon?: string;
    buttonAction?: string;
    additionalClassNames?: string[];
    isDraft?: boolean;
    reverseDependencyTo?: string;
}

export interface IFieldAction {
    name?: EFieldActionType;
    type?: string;
}

export interface IComparisionFormFieldMeta extends IFieldMeta {
    renderPosition?: 'up';
}

export interface ITabsMeta {
    title: string;
    key: string;
    /**
     * Key филдов входящих в Tabs
     */
    fieldKeys: string[];
}

export interface IWidgetsConfigMeta {
    widgetsTitle?: string;
    widgetsLayout?: Array<IWidgetsLayout>[];
    dictionariesTitle?: string;
    dictionariesLayout?: Array<IWidgetsLayout>[];
}

export interface IWidgetsLayout {
    title?: string;
    buttons?: Array<IWidgetsButtons>;
    statisticsFieldKey?: string;
    url?: string;
    icon?: string;
}

export interface IWidgetsButtons {
    position: string;
    link: string;
    preset: string;
}

export interface IGenerateOptionsRulesProps {
    from?: number; //  нижняя граница для генерации значений
    to?: number; // верхняя граница для генерации значений
    dynamicFrom?: string; // имя ключа, откуда нужно подтянуть нижнюю границу
    dynamicTo?: string; // имя ключа, откуда нужно подтянуть верхнюю границу
    step?: number; // шаг итерации от from до to
    labelStructure?: string; // структура подписи, по умолчанию - значение текущей итерации от from до to
    valueStructure?: string; // структура значения, по умолчанию - значение текущей итерации от from до to
}

export interface IAdditionalInputOptions {
    isFiltrationEnabled?: boolean;
    key: string;
    label: string;
    value?: string | boolean;
    onNotCreateKey?: boolean;
    displayFieldKey?: string;
    dependentInputKey?: string;
}

export interface IReferenceParamKey { // Ключи значения которых будут в качестве параметра для lookup
    constant?: string;
    from: string;
    to?: string;
}

export interface IFieldAvailabilityCondition {
    fieldName: string;
    fieldValues: string[];
}

export interface IAdditionalFields extends IFieldMeta {
    key: string;
    isRequired: boolean;
    id: number | string;
    displayOrder: number;
    defaultValue: string | number | boolean;
}

export type TMetadataSetAllPayload = {
    metadata: IEntityMeta[];
}

export type TMetadataSetPayload = {
    entityType: EEntityType;
    entityName: string;
    metadata: IEntityMeta;
}

export type TFormDraftMetadataPayload= {
    entityName: string;
    isDraft: boolean;
}

export type TMetadataResetPayload = {
    entityName: string;
    entityType: string;
    loading: boolean;
}
export type TMetadataSetLoadingPayload = {
    entityName: string;
}

export type TRequestMetadataSetPayload = {
    entityType: EEntityType;
    entityName: string;
    requestType: string;
    metadata: IRequestFormEntityMeta;
}

export interface IRequestsMetadataSetPayload {
    entityName: string;
    metadata: IEntityMeta;
}

export type TReferenceResolver = (entity: TEntity) => TEntity & {
    value: number | string;
    label: string;
};

export interface IRenderProperties {
    authorized?: boolean;
}

export interface ISubsectionMenu {
    key: string;
    title: string;
    referenceUrl?: string;
    order?: number;
}

export interface IDynamicTitle {
    edit?: string;
    create?: string;
}

export interface IValidationRules {
    mask?: string;
    maxNumericalValue?: number;
    minNumericalValue?: number;
    maxValueLength?: number;
    minValueLength?: number;
    regExpKey?: string;
    regExp?: string;
    errorMessage?: string;
    referenceUrl?: string;
}

export interface IRedirectOnCondition {
    // ключ для формирования ссылки, по умолчанию id
    to?: string;
    // ключ проверяемого поля
    target: string;
    // проверяемое значение
    value: string;
}
